"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Shared with the inline no-flash script in `resources/views/inertia_layout.edge`.
 * Both read/write this exact key — keep them in sync if it ever changes.
 */
export const THEME_STORAGE_KEY = "theme";

export type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

const THEMES: readonly Theme[] = ["light", "dark", "system"];

/**
 * `localStorage` THROWS — it does not merely return null — in a private window
 * with site data blocked, or when an extension revokes access. Every read and
 * write goes through these wrappers so that never reaches the page.
 */
/**
 * The choice made in THIS tab when storage refused to keep it.
 *
 * Without this, a blocked store made `setTheme` half-work: `applyTheme` stamped
 * `<html data-theme>` correctly, but the next `readStoredTheme` still answered
 * "system", so React's state disagreed with the DOM — the page went dark while
 * the toggle went on showing "Systemowy" as the checked item.
 *
 * It is deliberately NOT a cache in front of storage. It is consulted only when
 * a read actually throws, and any successful read clears it, so a value written
 * by another tab always wins over a stale local one if access is later restored.
 */
let unstoredTheme: Theme | null = null;

function readStoredTheme(): Theme {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    // Storage answered, so it is authoritative from here on.
    unstoredTheme = null;
    return (THEMES as string[]).includes(value ?? "")
      ? (value as Theme)
      : "system";
  } catch {
    return unstoredTheme ?? "system";
  }
}

function writeStoredTheme(theme: Theme): void {
  try {
    if (theme === "system") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
    unstoredTheme = null;
  } catch {
    // Storage unavailable (private window, blocked site data, extension). The
    // preference cannot persist across reloads, but it must still hold for the
    // rest of THIS page's life rather than silently reverting.
    unstoredTheme = theme;
  }
}

function prefersDark(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? (prefersDark() ? "dark" : "light") : theme;
}

function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme);
  document.documentElement.setAttribute("data-theme", resolved);
}

function getServerSnapshot(): Theme {
  return "system";
}

function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  const onStorage = (event: StorageEvent): void => {
    if (event.key === THEME_STORAGE_KEY || event.key === null) callback();
  };

  let mql: MediaQueryList | undefined;
  try {
    mql = window.matchMedia("(prefers-color-scheme: dark)");
    mql.addEventListener("change", callback);
  } catch {
    mql = undefined;
  }

  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
    mql?.removeEventListener("change", callback);
  };
}

/**
 * Reads and writes the visitor's theme preference (`light` / `dark` /
 * `system`), keeping `<html data-theme>` — which the CSS in `app.css` keys
 * off — in sync. When storage is unavailable the choice still holds for the
 * life of the page (see `unstoredTheme`); only persistence is lost.
 */
export function useTheme(): {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
} {
  const theme = useSyncExternalStore(
    subscribe,
    readStoredTheme,
    getServerSnapshot,
  );

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    writeStoredTheme(next);
    applyTheme(next);
    // useSyncExternalStore only re-renders on a "storage" event, which does
    // not fire in the tab that made the change — dispatch one so every
    // consumer of the hook in this tab picks up the new value too.
    window.dispatchEvent(new StorageEvent("storage", { key: THEME_STORAGE_KEY }));
  }, []);

  return { theme, resolvedTheme: resolveTheme(theme), setTheme };
}
